import { Box, MenuItem, Select, Skeleton, Tooltip } from '@mui/material';
import { QuestionIcon } from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { PhoneNumberFunction } from 'primary-schema';
import { useId } from 'react';

import { Alert, ExternalLink, FormField, api, getQueryProps, isDefined } from 'common';

import { formatPhoneNumber } from '../../../../../../../utils/string';
import { useFlowDefinitionContext } from '../../../../context';
import { useReactFlowViewContext } from '../../../context/context';
import { useSelectedNodeField } from '../../ConfigFields/useSelectedNodeField';

const CUSTOM_VALUE = '';

export function CallerPhoneNumberFormField() {
  const { flowDefinition } = useFlowDefinitionContext();
  const { organizationId } = flowDefinition;
  const { isEditable } = useReactFlowViewContext();

  const { value, setValue, visibleError } = useSelectedNodeField<string | null>(
    'config.callerPhoneNumberId'
  );
  const { value: agentPhoneNumber, setValue: setAgentPhoneNumber } = useSelectedNodeField<
    string | undefined
  >('config.agentPhoneNumber');
  const isCustomConfigured = !isDefined(value) && isDefined(agentPhoneNumber);
  const fieldId = useId();
  const {
    data: phoneNumberConfigurations,
    isPending: isPhoneNumberConfigurationsPending,
    isError,
    error,
  } = useQuery({
    ...getQueryProps(api.client.AssistantPhoneNumberConfiguration, 'findAll', {
      organizationIds: [organizationId],
      includeShared: true,
      supportedFunctions: [PhoneNumberFunction.InboundSMSConversation],
    }),
  });

  if (isPhoneNumberConfigurationsPending) {
    return <Skeleton variant="rectangular" height={40} />;
  }

  if (isError) {
    return <Alert error={error} title="Failed to load phone number configurations" />;
  }

  return (
    <FormField
      id={fieldId}
      label="Sender Phone Number"
      labelAdornment={
        <Tooltip
          placement="top"
          arrow
          title={
            <span>
              Phone number to receive SMS conversations on. Manage phone numbers in the{' '}
              <ExternalLink to="/phone-numbers" color="inherit">
                Phone Numbers
              </ExternalLink>{' '}
              page.
            </span>
          }
        >
          <Box>
            <QuestionIcon />
          </Box>
        </Tooltip>
      }
      error={visibleError}
    >
      <Select
        id={fieldId}
        disabled={!isEditable}
        value={value ?? CUSTOM_VALUE}
        displayEmpty
        onChange={(event) => {
          if (event.target.value === CUSTOM_VALUE) {
            setValue(null);
            return;
          }
          setValue(event.target.value);
          setAgentPhoneNumber(undefined);
        }}
      >
        <MenuItem value={CUSTOM_VALUE} disabled={!isCustomConfigured}>
          <em>{isCustomConfigured ? 'Custom' : 'Select a phone number'}</em>
        </MenuItem>
        {phoneNumberConfigurations.map((phoneNumberConfiguration) => (
          <MenuItem key={phoneNumberConfiguration.id} value={phoneNumberConfiguration.id}>
            {phoneNumberConfiguration.displayName}
            {` [${formatPhoneNumber(phoneNumberConfiguration.phoneNumber)}]`}
          </MenuItem>
        ))}
      </Select>
    </FormField>
  );
}
